<template>
  <div class="provider-applications-closed">
    <div v-if="loading">
      <div v-for="value in 3" :key="value" class="animate-pulse bg-grey-divider-subdued h-112 w-full mb-16" />
    </div>
    <template v-else>
      <div
        class="mb-16 flex flex-wrap gap-8 transition-opacity"
        data-testid="provider-applications-closed-filter-row"
        :class="{ 'pointer-events-none opacity-60': applyingFilters }"
      >
        <filter-token
          :button-label="careTypeButtonLabel"
          :selected="careTypeFilter.isApplied"
          modal-label="Care type"
          class="!my-0"
        >
          <radio-list-filter-panel
            slot-scope="{ closeModal }"
            :options="careTypeFilterOptions"
            :selectedValue="careTypeFilterSelectedValue"
            radio-group-name="Filter by care type"
            :closeModal="closeModal"
            @filterApplied="onCareTypeFilterApplied"
          />
        </filter-token>
        <filter-token
          :button-label="statusButtonLabel"
          :selected="statusFilter.isApplied"
          modal-label="Application status"
          class="!my-0"
        >
          <radio-list-filter-panel
            slot-scope="{ closeModal }"
            :options="statusFilterOptions"
            :selectedValue="statusFilterSelectedValue"
            radio-group-name="Filter by application status"
            :closeModal="closeModal"
            @filterApplied="onStatusFilterApplied"
          />
        </filter-token>
      </div>
      <div v-if="applyingFilters" data-testid="applying-filters-skeleton" role="status" aria-live="polite" aria-busy="true">
        <div v-for="value in 3" :key="value" class="animate-pulse bg-grey-divider-subdued h-112 w-full mb-16" />
      </div>
      <div v-else-if="hasApplications" data-testid="provider-applications-closed-application-list">
        <ProviderApplicationCard
          v-for="application in applications"
          :key="application.id"
          :application="application"
          :displayAsActive="false"
          class="mb-16"
        />
        <Button
          v-if="hasNextPage"
          variant="secondary"
          class="cursor-pointer w-full mb-64 sm:flex sm:w-auto sm:mx-auto"
          :loading="loadingMore"
          @click.prevent="loadMore"
        >
          View More
        </Button>
      </div>
      <div v-else-if="hasFiltersApplied" data-testid="provider-applications-closed-empty-filtered">
        <h2 class="text-h4 mb-8">No Applications Found</h2>
        <p class="text-body">Try updating your filters</p>
      </div>
      <div v-else data-testid="provider-applications-closed-empty-default">
        <h2 class="text-h4 mb-8">No Closed Jobs</h2>
        <p class="text-body">Jobs you've applied for that have closed will appear here</p>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Button from '@/components/button/Button.vue';
import FilterToken from '@/components/filter_token/FilterToken.vue';
import RadioListFilterPanel from '@/components/radioButtonGroup/RadioListFilterPanel.vue';
import ProviderApplicationCard from './ProviderApplicationCard.vue';
import { FETCH_PROVIDER_CLOSED_APPLICATIONS } from '@/graphql/account/queries';
import { useUrlQueryParamFilter } from '@/composables/useUrlQueryParamFilter';

const CARE_TYPE_FILTER_ANY = '__ANY__';

const CARE_TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: CARE_TYPE_FILTER_ANY, label: 'Any' },
  { value: 'BABYSITTER', label: 'Babysitter' },
  { value: 'NANNY', label: 'Nanny' },
  { value: 'PET_CARE', label: 'Pet care' },
  { value: 'COMPANION_CARE', label: 'Companion care' },
  { value: 'HOUSEKEEPING', label: 'Housekeeping' },
];

const ALLOWED_CARE_TYPE_VALUES = new Set(
  CARE_TYPE_FILTER_OPTIONS.filter((o) => o.value !== CARE_TYPE_FILTER_ANY).map((o) => o.value)
);

const STATUS_FILTER_ANY = '__ANY__';
const STATUS_FILTER_CLOSED_GROUP = 'CLOSED_GROUP';
const STATUS_FILTER_NOT_A_FIT = 'NOT_A_FIT';

const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: STATUS_FILTER_ANY, label: 'Any status' },
  { value: STATUS_FILTER_CLOSED_GROUP, label: 'Closed' },
  { value: STATUS_FILTER_NOT_A_FIT, label: 'Not a fit' },
];

function statusesForGraphQL(committedStatusFilter: string | null): string[] {
  switch (committedStatusFilter) {
    case STATUS_FILTER_CLOSED_GROUP:
      return ['CLOSED', 'EXPIRED'];
    case STATUS_FILTER_NOT_A_FIT:
      return ['DISMISSED'];
    default:
      return ['CLOSED', 'EXPIRED', 'DISMISSED'];
  }
}

export default defineComponent({
  name: 'ProviderApplicationsClosed',
  components: { Button, FilterToken, RadioListFilterPanel, ProviderApplicationCard },
  setup() {
    const careTypeFilter = useUrlQueryParamFilter('careType', {
      toQuery: (v) => v.toLowerCase(),
      fromQuery: (v) => {
        const upper = v.toUpperCase();
        return ALLOWED_CARE_TYPE_VALUES.has(upper) ? upper : null;
      },
    });

    const statusFilter = useUrlQueryParamFilter('status', {
      toQuery: (v) => (v === STATUS_FILTER_CLOSED_GROUP ? 'closed' : 'not-a-fit'),
      fromQuery: (v) => {
        const key = v.toLowerCase();
        if (key === 'closed') return STATUS_FILTER_CLOSED_GROUP;
        if (key === 'not-a-fit') return STATUS_FILTER_NOT_A_FIT;
        return null;
      },
    });

    return { careTypeFilter, statusFilter };
  },
  apollo: {
    viewer: {
      query: FETCH_PROVIDER_CLOSED_APPLICATIONS,
      variables(): { careType: string | null; statuses: string[] } {
        return {
          careType: this.careTypeFilter.value,
          statuses: statusesForGraphQL(this.statusFilter.value),
        };
      },
      fetchPolicy: 'network-only',
      result() {
        this.totalCount = this.viewer?.jobApplications?.totalCount ?? 0;
        this.lastPageInfo = this.viewer?.jobApplications?.pageInfo ?? null;
        this.applications = (this.viewer?.jobApplications?.edges ?? []).map((edge: { node: any }) => edge.node);
        this.loading = false;
        this.applyingFilters = false;
      },
      error() {
        this.loading = false;
        this.applyingFilters = false;
      },
    },
  },
  data(): {
    totalCount: number;
    applications: any[];
    loading: boolean;
    loadingMore: boolean;
    applyingFilters: boolean;
    viewer: any;
    lastPageInfo: { hasNextPage: boolean; endCursor: string | null } | null;
    careTypeFilterOptions: { value: string; label: string }[];
    statusFilterOptions: { value: string; label: string }[];
  } {
    return {
      totalCount: 0,
      applications: [],
      loading: true,
      loadingMore: false,
      applyingFilters: false,
      viewer: null,
      lastPageInfo: null,
      careTypeFilterOptions: CARE_TYPE_FILTER_OPTIONS,
      statusFilterOptions: STATUS_FILTER_OPTIONS,
    };
  },
  computed: {
    hasApplications(): boolean {
      return this.totalCount > 0;
    },
    hasNextPage(): boolean {
      return !!this.lastPageInfo?.hasNextPage;
    },
    hasFiltersApplied(): boolean {
      return this.careTypeFilter.isApplied || this.statusFilter.isApplied;
    },
    careTypeFilterSelectedValue(): string {
      return this.careTypeFilter.value ?? CARE_TYPE_FILTER_ANY;
    },
    careTypeButtonLabel(): string {
      if (!this.careTypeFilter.value) return 'Care type';
      const match = CARE_TYPE_FILTER_OPTIONS.find((o) => o.value === this.careTypeFilter.value);
      return match ? match.label : 'Care type';
    },
    statusFilterSelectedValue(): string {
      return this.statusFilter.value ?? STATUS_FILTER_ANY;
    },
    statusButtonLabel(): string {
      if (!this.statusFilter.value) return 'Status';
      const match = STATUS_FILTER_OPTIONS.find((o) => o.value === this.statusFilter.value);
      return match ? match.label : 'Status';
    },
  },
  methods: {
    resetList(): void {
      this.applications = [];
      this.lastPageInfo = null;
      this.totalCount = 0;
    },
    onCareTypeFilterApplied({ value }: { value: string }): void {
      const next = value === CARE_TYPE_FILTER_ANY ? null : value;
      if (this.careTypeFilter.apply(next)) {
        this.resetList();
        this.applyingFilters = true;
      }
    },
    onStatusFilterApplied({ value }: { value: string }): void {
      let next: string | null = null;
      if (value === STATUS_FILTER_CLOSED_GROUP || value === STATUS_FILTER_NOT_A_FIT) {
        next = value;
      }
      if (this.statusFilter.apply(next)) {
        this.resetList();
        this.applyingFilters = true;
      }
    },
    getLastCursor(): string | null {
      return this.lastPageInfo?.endCursor ?? null;
    },
    async loadMore() {
      const after = this.getLastCursor();
      if (!after || this.loadingMore || this.applyingFilters) return;

      this.loadingMore = true;

      try {
        const { data } = await this.$apollo.query({
          query: FETCH_PROVIDER_CLOSED_APPLICATIONS,
          variables: {
            after,
            careType: this.careTypeFilter.value,
            statuses: statusesForGraphQL(this.statusFilter.value),
          },
          fetchPolicy: 'network-only',
        });
        const connection = data?.viewer?.jobApplications;
        this.lastPageInfo = connection?.pageInfo ?? null;
        const newNodes = (connection?.edges ?? []).map((edge: { node: any }) => edge.node);
        this.applications = [...this.applications, ...newNodes];
      } catch (error) {
        console.error('Error loading more closed applications:', error);
      } finally {
        this.loadingMore = false;
      }
    },
  },
});
</script>
